import {
  CardPaymentPartialState,
  CardPaymentSelectors,
} from '@common/data-access-card-payment';
import {
  CheckoutPartialState,
  CheckoutSelectors,
} from '@common/data-access-checkout';
import { FeatureConfigPartialState } from '@common/data-access-feature-config';
import { PersonalDetailsSelectors } from '@common/data-access-personal-details';
import {
  QuotesPageSelectors,
  QuotesPartialState,
  QuotesSelectors,
} from '@common/data-access-quotes';
import {
  SelectPaymentPartialState,
  SelectPaymentSelectors,
} from '@common/data-access-select-payment';
import {
  UserProfilePageSelectors,
  UserProfilePartialState,
  UserProfileSelectors,
} from '@common/data-access-user-profile';
import {
  BasketItem,
  BasketItemApplianceDetails,
  CheckoutCompletedItem,
  CheckoutConfirmationData,
  CheckoutConfirmationTaggingData,
  CheckoutPlanDetails,
  Cover,
  CoverPaymentOptions,
  CoverType,
  getDigitalIdFromUser,
  PaymentType,
  Quote,
  TaggingMetadata,
} from '@common/util-models';
import { selectUser } from '@domgen/dgx-fe-auth';
import { User } from '@domgen/dgx-fe-auth/lib/common/models/user';
import { createSelector } from '@ngrx/store';
import { mapMultiPlanDetails } from '../utils/checkout-order-confirmation.utils';

export const getFirstCheckedOutItem = createSelector(
  CheckoutSelectors.getCheckedOutItems,
  (quotes: CheckoutCompletedItem[]) => (quotes.length ? quotes[0] : undefined)
);

export const getPersonalDetails = createSelector(
  UserProfilePageSelectors.getYourDetails,
  PersonalDetailsSelectors.getPersonalDetailsWithAddress,
  (loggedInUserDetails, guestUserDetails) =>
    loggedInUserDetails?.address?.addressLine1
      ? loggedInUserDetails
      : guestUserDetails
);

export const getPreferredPaymentDay = createSelector(
  PersonalDetailsSelectors.getPreferredPaymentDay,
  UserProfileSelectors.getPreferredPaymentDay,
  UserProfileSelectors.getPersonalDetails,
  (paymentDayGuest, paymentDayUser, userProfile) => {
    return userProfile !== undefined ? paymentDayUser : paymentDayGuest;
  }
);

export const getCheckoutConfirmation = createSelector<
  CheckoutPartialState &
    UserProfilePartialState &
    QuotesPartialState &
    SelectPaymentPartialState,
  CheckoutCompletedItem | undefined,
  PaymentType | undefined,
  CoverType | undefined,
  boolean | undefined,
  number | undefined,
  CoverPaymentOptions | undefined,
  CheckoutConfirmationData
>(
  getFirstCheckedOutItem,
  SelectPaymentSelectors.getSelectedPaymentType,
  QuotesSelectors.getSelectedCoverType,
  QuotesSelectors.getCurrentItemIsHeatingAppliance,
  getPreferredPaymentDay,
  QuotesSelectors.getPaymentOptions,
  (
    checkoutData,
    paymentType,
    coverType,
    isHeating,
    preferredPaymentDay,
    paymentOptions
  ) => {
    const heading = isHeating
      ? 'Your maintenance and support plan'
      : 'Your appliance protection';
    const applianceDetails = checkoutData
      ? ({
          ...checkoutData.item.data.applianceDetails,
        } as BasketItemApplianceDetails)
      : undefined;

    return {
      ...checkoutData?.confirmation,
      ...applianceDetails,
      heading,
      paymentType,
      coverType,
      isHeating,
      preferredPaymentDay,
      itemQuoteDetails: checkoutData?.item.data.quotes[0],
      paymentOptions,
    } as CheckoutConfirmationData;
  }
);

export const getPlanDetails = createSelector<
  CheckoutPartialState &
    UserProfilePartialState &
    QuotesPartialState &
    SelectPaymentPartialState,
  CheckoutCompletedItem | undefined,
  Cover | undefined,
  number | undefined,
  PaymentType | undefined,
  CheckoutPlanDetails
>(
  getFirstCheckedOutItem,
  QuotesSelectors.getSelectedCover,
  getPreferredPaymentDay,
  SelectPaymentSelectors.getSelectedPaymentType,
  (checkedOutItem, cover, paymentDay, paymentType) => {
    const quote = checkedOutItem?.item.data.quotes.find(
      (q) => q.quoteId === cover?.quoteId
    );
    const paymentOption = quote?.paymentOptions.find(
      (paymentOption) => paymentOption.paymentType === paymentType
    );
    const confirmation = checkedOutItem?.confirmation;
    const applianceDetails = checkedOutItem?.item.data.applianceDetails;
    const periodOfCover = quote?.periodOfCover;
    const paymentTerm = quote?.paymentTerm;
    return {
      paymentDay,
      paymentOption,
      confirmation,
      applianceDetails,
      periodOfCover,
      itemType: checkedOutItem?.item.itemType,
      paymentTerm,
    };
  }
);

export const getMultiPlanDetails = createSelector<
  CheckoutPartialState &
    UserProfilePartialState &
    QuotesPartialState &
    SelectPaymentPartialState,
  CheckoutCompletedItem[] | undefined,
  Quote[],
  number | undefined,
  PaymentType | undefined,
  CoverType | null,
  CheckoutPlanDetails[] | undefined
>(
  CheckoutSelectors.getCheckedOutItems,
  QuotesPageSelectors.getMultiItemQuotes,
  getPreferredPaymentDay,
  SelectPaymentSelectors.getSelectedPaymentType,
  QuotesSelectors.getSelectedCoverType,
  (checkedOutItems, quotes, paymentDay, paymentType, selectedCoverType) =>
    mapMultiPlanDetails(
      checkedOutItems,
      quotes,
      paymentDay,
      paymentType,
      selectedCoverType
    )
);

const getIsHeatingAndCardNumber = createSelector<
  QuotesPartialState & CardPaymentPartialState,
  boolean,
  string | undefined,
  { isHeating: boolean; cardNumber: string | undefined }
>(
  QuotesSelectors.getCurrentItemIsHeatingAppliance,
  CardPaymentSelectors.getCardPaymentOrderNumber,
  (isHeating, cardNumber) => ({ isHeating, cardNumber })
);

export const getPageLandingTaggingData = createSelector<
  CheckoutPartialState &
    QuotesPartialState &
    SelectPaymentPartialState &
    CardPaymentPartialState &
    FeatureConfigPartialState,
  TaggingMetadata | undefined,
  BasketItem | undefined,
  Quote | undefined,
  string,
  User | undefined,
  PaymentType | undefined,
  { isHeating: boolean; cardNumber: string | undefined },
  CheckoutCompletedItem | undefined,
  CheckoutConfirmationTaggingData
>(
  QuotesPageSelectors.getTaggingMetadata,
  QuotesSelectors.getCurrentItem,
  QuotesPageSelectors.getQuote,
  QuotesSelectors.getSelectedCoverName,
  selectUser,
  SelectPaymentSelectors.getSelectedPaymentType,
  getIsHeatingAndCardNumber,
  getFirstCheckedOutItem,
  (
    taggingMetadata,
    basketItem,
    quote,
    coverName,
    user,
    paymentType,
    { isHeating, cardNumber },
    checkoutItem
  ) => {
    const paymentOptions = quote?.cover.paymentOptions;
    const digitalId = getDigitalIdFromUser(user);

    return {
      ...taggingMetadata,
      user,
      coverName,
      paymentType,
      basketId: basketItem?.basketId,
      fee:
        paymentType === PaymentType.Card
          ? paymentOptions?.card?.fee
          : paymentOptions?.directDebit?.subsequentPayment,
      planNumber: checkoutItem?.confirmation.planNumber,
      digitalId,
      isHeating,
      cardNumber,
    } as CheckoutConfirmationTaggingData;
  }
);
